import { ColumnDef, ColumnSort, type PaginationState, type SortingState, Updater, type VisibilityState, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import axios from 'axios';
import React from 'react';
import { useBoolean } from '../shared/hooks/use-boolean';
import { useWeights } from '../shared/hooks/use-weights';
import { type ReportRes, type ReportRunOptions } from './report-types';
import { getUserSettings } from './report-utils';

const defaultPagination: PaginationState = {
    pageIndex: 0,
    pageSize: 50,
}

export const useReports = <T>(
    defaultSort: ColumnSort,
    columns: ColumnDef<T, any>[], // can't get TypeScript to figure out the 6+ column types that will be retured from createColumnHelper in react-table
    reportUrl: URL,
    reportParams: URLSearchParams,
    enableSorting: boolean,
) => {
    const [data, setData] = React.useState<ReportRes | null>(null);
    const [pagination, setPagination] = React.useState<PaginationState>(defaultPagination);
    const [sorting, setSorting] = React.useState<SortingState>([defaultSort]);
    const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({cargo_category: false});
    const [isLoading, startLoading, endLoading] = useBoolean(false);
    const { weightSystem } = useWeights();

    const runReport = async (runOptions: ReportRunOptions) => {
        !runOptions.csv && startLoading();
        const userSettings = getUserSettings();
        const pageIndex = runOptions.pageIndex === undefined ? pagination.pageIndex : runOptions.pageIndex;
        const pageSize = runOptions.pageSize || pagination.pageSize;

        const sortColumn = runOptions.sortColumn || sorting[0].id;
        const sortDirection = runOptions.sortDirection || (sorting[0].desc ? 'desc' : 'asc');

        reportParams.set('language', userSettings.language);
        reportParams.set('timezone', userSettings.timezone);
        reportParams.set('unitsDistance', userSettings.units);
        reportParams.set('unitsWeight', weightSystem);
        reportParams.set('pageNumber', String(pageIndex + 1));
        reportParams.set('pageSize', String(pageSize));
        reportParams.set('sortColumn', sortColumn);
        reportParams.set('sortDirection', sortDirection);

        if (runOptions.csv) {
            reportParams.set('csv', 'true');
            // Force the report to start on page 1 and return a max of 1M records so the CSV results aren't truncated by the UI pagination
            reportParams.set('pageNumber', String(1));
            reportParams.set('pageSize', String(1000000));
            window.location.href = `${reportUrl.toString()}?${reportParams.toString()}`;
        } else {
            const url = `${reportUrl.toString()}?${reportParams.toString()}`
            const reportData = (await axios.get<ReportRes>(url)).data;
            setData(reportData);
        }
        !runOptions.csv && endLoading();
    }

    const onPaginationChange = (updater: Updater<PaginationState>) => {
        if (typeof updater === 'function') {
            const newPagination = updater(pagination);
            setPagination(newPagination);
            runReport({ pageIndex: newPagination.pageIndex, pageSize: newPagination.pageSize });
        }
    }

    const onSortingChange = (updater: Updater<SortingState>) => {
        if (typeof updater === 'function') {
            const newSort = updater(sorting);
            setSorting(newSort);
            runReport({ sortColumn: newSort[0].id, sortDirection: newSort[0].desc ? 'desc' : 'asc' });
        }
    }

    const table = useReactTable<T>({
        data: data ? data.data : [],
        columns,
        columnResizeMode: 'onChange',
        columnResizeDirection: 'ltr',
        getCoreRowModel: getCoreRowModel(),
        rowCount: data?.info.paging.totalRecords,
        pageCount: data?.info.paging.totalPages,
        state: {
            pagination,
            sorting,
            columnVisibility
        },
        onPaginationChange: onPaginationChange,
        manualPagination: true,
        enableSorting: enableSorting,
        enableSortingRemoval: false, // removes tri-state sort
        onSortingChange: onSortingChange,
        onColumnVisibilityChange: setColumnVisibility,
        // defaultColumn: {
        //     minSize: 1,
        //     size: 50,
        // },
        // debugTable: true,
        // debugHeaders: true,
        // debugColumns: true,
    });

    return {
        data,
        runReport,
        table,
        isLoading,
    }
}
